"use client";

import { IconInfo } from "@decibeltrade/react-icons";
import { useLocale, useTranslations } from "next-intl";
import { useMemo } from "react";
import { css } from "styled-system/css";
import { flex } from "styled-system/patterns";

import { Tooltip } from "@/components/tooltip";
import type { WeeklyEarningPoint } from "@/contexts/queries/rewards/use-rewards-summary";

import { bannerCardStyles, bannerLabelStyles } from "./banner-card-styles";
import { WeeklyTrendChart } from "./weekly-trend-chart";

interface WeeklyTrendCardProps {
  weeklyBreakdown: WeeklyEarningPoint[] | undefined;
}

export function WeeklyTrendCard({ weeklyBreakdown }: WeeklyTrendCardProps) {
  const t = useTranslations("rewards");
  const locale = useLocale();
  const fmtWeek = useMemo(() => {
    const dateFmt = new Intl.DateTimeFormat(locale, {
      month: "short",
      day: "numeric",
      timeZone: "UTC",
    });
    return (tsSec: number) => dateFmt.format(new Date(tsSec * 1000));
  }, [locale]);
  const hasData = !!weeklyBreakdown && weeklyBreakdown.length > 0;
  const first = weeklyBreakdown?.[0];
  const last = weeklyBreakdown?.[weeklyBreakdown.length - 1];

  return (
    <div className={bannerCardStyles}>
      <div className={flex({ justify: "space-between" })}>
        <div className={flex({ align: "center", gap: "4" })}>
          <span className={bannerLabelStyles}>{t("banner.last12Weeks")}</span>
          <Tooltip content={t("banner.last12WeeksTooltip")} placement="top">
            <span className={infoTriggerStyles}>
              <IconInfo className={infoIconStyles} />
            </span>
          </Tooltip>
        </div>
        <span className={bannerLabelStyles}>{t("banner.weekly")}</span>
      </div>
      <div className={chartWrapperStyles}>
        <WeeklyTrendChart data={weeklyBreakdown} />
      </div>
      {hasData && first && last && (
        <div className={flex({ mt: "12", justify: "space-between" })}>
          <span className={bottomLabelStyles}>{fmtWeek(first.weekStartTsSec)}</span>
          <span className={bottomLabelStyles}>{fmtWeek(last.weekStartTsSec)}</span>
        </div>
      )}
    </div>
  );
}

const chartWrapperStyles = css({ mt: "16", h: "72", w: "full", minW: "0" });

const bottomLabelStyles = css({
  textStyle: "highlight.md",
  color: "text.disabled",
});

const infoTriggerStyles = css({
  display: "inline-flex",
  color: "text.tertiary",
  cursor: "help",
});

const infoIconStyles = css({ w: "12", h: "12" });
